/**
 * Sprint API endpoints - Complete implementation
 */
import axiosClient from './axiosClient';
import { Sprint, CreateSprintData } from '@/types';

export interface UpdateSprintData {
  name?: string;
  description?: string;
  goal?: string;
  start_date?: string;
  end_date?: string;
  capacity?: number;
  status?: string;
}

export interface SprintWithTasks extends Sprint {
  tasks: any[];
  by_status?: Record<string, any[]>;
}

// TTL cache + in-flight dedup for getSprint. The Tasks page renders one
// TaskCard per task, and each card calls getSprint(task.sprint_id). Many
// tasks share a sprint, so without this every card produced its own HTTP
// request. With dedup, N concurrent calls for the same sprint share one
// network round-trip; subsequent calls within the TTL hit the cache.
const SPRINT_TTL_MS = 60_000;
const sprintCache = new Map<string, { ts: number; data: SprintWithTasks }>();
const inFlightSprint = new Map<string, Promise<SprintWithTasks>>();

export const sprintAPI = {
  /**
   * Get sprints with optional project filter
   */
  getSprints: async (projectId?: string, isActive?: boolean): Promise<Sprint[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (isActive !== undefined) params.is_active = isActive;
    
    const response = await axiosClient.get<Sprint[]>('/sprints', { params });
    return response.data;
  },

  /**
   * Get active sprints for a project
   */
  getActiveSprints: async (projectId: string): Promise<Sprint[]> => {
    const response = await axiosClient.get<Sprint[]>('/sprints', {
      params: { project_id: projectId, is_active: true },
    });
    return response.data;
  },

  /**
   * Get sprint by ID with tasks
   */
  getSprint: async (sprintId: string, includeTasks: boolean = true): Promise<SprintWithTasks> => {
    const key = `${sprintId}:${includeTasks ? '1' : '0'}`;
    const cached = sprintCache.get(key);
    if (cached && Date.now() - cached.ts < SPRINT_TTL_MS) return cached.data;

    const existing = inFlightSprint.get(key);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const response = await axiosClient.get<SprintWithTasks>(
          `/sprints/${sprintId}`,
          { params: { include_tasks: includeTasks } }
        );
        sprintCache.set(key, { ts: Date.now(), data: response.data });
        return response.data;
      } finally {
        inFlightSprint.delete(key);
      }
    })();
    inFlightSprint.set(key, promise);
    return promise;
  },

  /** Invalidate the in-memory sprint cache (call after sprint mutations). */
  invalidateSprintCache: (sprintId?: string) => {
    if (!sprintId) {
      sprintCache.clear();
      return;
    }
    Array.from(sprintCache.keys())
      .filter((k) => k.startsWith(`${sprintId}:`))
      .forEach((k) => sprintCache.delete(k));
  },

  /**
   * Create new sprint
   */
  createSprint: async (data: CreateSprintData): Promise<Sprint> => {
    const response = await axiosClient.post<Sprint>('/sprints', data);
    return response.data;
  },

  /**
   * Update sprint
   */
  updateSprint: async (sprintId: string, data: UpdateSprintData): Promise<Sprint> => {
    const response = await axiosClient.put<Sprint>(`/sprints/${sprintId}`, data);
    return response.data;
  },

  /**
   * Delete sprint
   */
  deleteSprint: async (sprintId: string): Promise<void> => {
    await axiosClient.delete(`/sprints/${sprintId}`);
  },

  /**
   * Start/activate sprint
   */
  startSprint: async (sprintId: string): Promise<Sprint> => {
    const response = await axiosClient.post<Sprint>(`/sprints/${sprintId}/start`);
    return response.data;
  },

  /**
   * Complete sprint
   */
  completeSprint: async (
    sprintId: string,
    moveToSprintId?: string
  ): Promise<Sprint> => {
    const response = await axiosClient.post<Sprint>(
      `/sprints/${sprintId}/complete`,
      { move_to_sprint_id: moveToSprintId }
    );
    return response.data;
  },

  /**
   * Get sprint tasks
   */
  getSprintTasks: async (sprintId: string, grouped: boolean = false): Promise<any> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/tasks`, {
      params: { grouped },
    });
    return response.data;
  },

  /**
   * Add task to sprint
   */
  addTaskToSprint: async (sprintId: string, taskId: string): Promise<void> => {
    await axiosClient.post(`/sprints/${sprintId}/tasks`, { task_id: taskId });
  },

  /**
   * Remove task from sprint
   */
  removeTaskFromSprint: async (sprintId: string, taskId: string): Promise<void> => {
    await axiosClient.delete(`/sprints/${sprintId}/tasks/${taskId}`);
  },
};


